import React, { useEffect, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { deleteQorSavedCard } from "../../apollo/server";
import { BrandBadge, cardExpiry, cardTitle } from "./cardBrand";
import QorPaymentForm from "./QorPaymentForm";
import QorWalletButtons from "./QorWalletButtons";

const DELETE_CARD = gql`${deleteQorSavedCard}`;

const ACCENT = "#d9232f";

/**
 * Payment sheet — the DoorDash payment modal for QorCommerce.
 *
 * Two views in one dialog:
 *   "list" — Saved payment methods (tap to make it the one you pay with, trash
 *            to remove) followed by "Add new payment method".
 *   "add"  — the QorCommerce hosted card form (+ Apple/Google Pay when this was
 *            opened to pay right now), with Back returning to the list.
 *
 * The sheet only SELECTS a card; the charge happens when Place Order runs in
 * checkout. The one exception is `purpose="pay"`, which is how the sheet opens
 * when Place Order was pressed with no card on file — there a freshly tokenized
 * card pays immediately, so the customer isn't asked to press the button twice.
 */
export default function QorPaymentSheet({
  open,
  onClose,
  cards = [],
  loading = false,
  selectedToken,
  onSelect,
  onCardsChanged,
  onNewCard,
  profileId,
  amount,
  onError,
  purpose = "manage",
}) {
  const hasCards = cards.length > 0;
  const [view, setView] = useState("list");

  // Opening with nothing on file goes straight to the card form — there is no
  // list to look at yet.
  useEffect(() => {
    if (open) setView(hasCards ? "list" : "add");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const [removeCard, { loading: removing }] = useMutation(DELETE_CARD, {
    onCompleted: () => {
      if (typeof onCardsChanged === "function") onCardsChanged();
    },
  });

  const rowSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.75,
    py: 1.5,
    cursor: "pointer",
    borderBottom: "1px solid #EDEFF2",
    transition: "background-color .15s",
    "&:hover": { backgroundColor: "#FAFAFA" },
    "&:last-of-type": { borderBottom: "none" },
  };

  const sectionLabelSx = {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#4B5563",
    mb: 0.5,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5 }}>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ ml: -0.5, color: "#111827" }}
          aria-label="Close payment"
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
        <Typography sx={{ mt: 1, fontWeight: 800, fontSize: "1.6rem", color: "#111827" }}>
          Payment
        </Typography>
        {purpose === "pay" && amount && (
          <Typography variant="body2" sx={{ mt: 0.5, color: "#6b7280" }}>
            You&apos;ll be charged <b>${amount}</b> when you place the order.
          </Typography>
        )}
      </Box>

      <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 2, pb: 1, maxHeight: "62vh", overflowY: "auto" }}>
        {view === "list" ? (
          <>
            {loading && !hasCards && (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <CircularProgress size={22} sx={{ color: ACCENT }} />
              </Box>
            )}

            {hasCards && (
              <>
                <Typography sx={sectionLabelSx}>Saved payment methods</Typography>
                <Box sx={{ mb: 2 }}>
                  {cards.map((card) => {
                    const selected = selectedToken === card.token;
                    return (
                      <Box
                        key={card.token}
                        sx={rowSx}
                        onClick={() => onSelect && onSelect(card.token)}
                      >
                        <BrandBadge brand={card.brand} size="sm" />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{ fontWeight: 600, fontSize: "1rem", color: "#111827", lineHeight: 1.35 }}
                          >
                            {cardTitle(card)}
                          </Typography>
                          {card.expiry && (
                            <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>
                              {cardExpiry(card)}
                            </Typography>
                          )}
                        </Box>
                        {selected ? (
                          <CheckIcon sx={{ fontSize: 24, color: "#0B8B3E" }} />
                        ) : (
                          <Tooltip title="Remove card">
                            <span>
                              <IconButton
                                size="small"
                                disabled={removing}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeCard({
                                    variables: { token: card.token, last4: card.last4 },
                                  });
                                }}
                                sx={{
                                  bgcolor: "#F3F4F6",
                                  color: "#4B5563",
                                  "&:hover": { bgcolor: "#E5E7EB", color: "#dc2626" },
                                }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Divider sx={{ borderColor: "#EDEFF2", mb: 2 }} />
              </>
            )}

            <Typography sx={sectionLabelSx}>Add new payment method</Typography>
            <Box sx={rowSx} onClick={() => setView("add")}>
              <CreditCardIcon sx={{ fontSize: 26, color: "#374151" }} />
              <Typography sx={{ flex: 1, fontWeight: 600, fontSize: "1rem", color: "#111827" }}>
                Credit/Debit Card
              </Typography>
              <ChevronRightIcon sx={{ color: "#6B7280" }} />
            </Box>
          </>
        ) : (
          <>
            {purpose === "pay" && (
              <QorWalletButtons amount={amount} onWallet={onNewCard && ((p) => onNewCard(p.token, p.wallet))} onError={onError} />
            )}
            {/* profile_id vaults the card under the user's account, so it shows
                in Saved payment methods next time — saving is implicit. */}
            <QorPaymentForm
              profileId={profileId}
              onToken={(token) => onNewCard && onNewCard(token)}
              onError={onError}
            />
            <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af", mt: 0.5 }}>
              Your card is saved securely to your account for faster checkout next time.
            </Typography>
          </>
        )}
      </Box>

      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: "1px solid #EDEFF2",
          backgroundColor: "#fff",
        }}
      >
        <Button
          fullWidth
          disableElevation
          onClick={() => {
            // From the card form, Back returns to the list when there is one to
            // return to; otherwise it closes the sheet.
            if (view === "add" && hasCards) setView("list");
            else onClose();
          }}
          sx={{
            height: 50,
            borderRadius: "999px",
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 700,
            color: "#111827",
            backgroundColor: "#EEEFF1",
            "&:hover": { backgroundColor: "#E3E5E8" },
          }}
        >
          {view === "add" && hasCards ? "Back" : "Done"}
        </Button>
      </Box>
    </Dialog>
  );
}
